// AniList GraphQL API service layer with caching
// All requests are proxied through our backend server to avoid CORS issues.

import axios from 'axios';
import { ANILIST_PROXY_URL } from '../config/constants';

// Create a DEDICATED axios instance for AniList API calls (via proxy).
// This is completely isolated from the global axios instance which has
// the X-API-Key interceptor for backend calls. Using a separate instance
// ensures NO backend headers/interceptors can accidentally leak into AniList requests.
const anilistAxios = axios.create();

/**
 * Make GraphQL query to AniList API (proxied through our backend)
 * @param {string} query - GraphQL query string
 * @param {object} variables - Query variables
 * @param {string} token - Optional OAuth token
 * @returns {Promise} API response
 */
const makeGraphQLRequest = async (query, variables = {}, token = null) => {
  try {
    // Send to our backend proxy which forwards to AniList.
    // Token is passed in the body so the proxy can set the Authorization header.
    const response = await anilistAxios.post(
      ANILIST_PROXY_URL,
      {
        query,
        variables,
        ...(token ? { token } : {})
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      }
    );

    if (response.data.errors) {
      throw new Error(response.data.errors[0].message);
    }

    return response.data.data;
  } catch (error) {
    if (error.response) {
      // Rate limiting - preserve response for retry logic
      if (error.response.status === 429) {
        const err = new Error('Rate limit exceeded. Please wait a moment and try again.');
        err.response = error.response; // Preserve status code!
        throw err;
      }
      // Invalid token - preserve response
      if (error.response.status === 401) {
        const err = new Error('Invalid token. Please check your AniList token.');
        err.response = error.response;
        throw err;
      }
      // Server error - preserve response for retry logic
      if (error.response.status === 500) {
        const err = new Error('Server error (500). The operation may have succeeded.');
        err.response = error.response;
        throw err;
      }
    }
    throw error;
  }
};

/**
 * Generic retry wrapper for AniList API mutations.
 * - 429 (Rate limit): Infinite retry with exponential backoff (caps at 60s)
 * - 500 (Server error): Treated as success (AniList usually processes the mutation)
 * - Network errors: Infinite retry with exponential backoff (caps at 30s)
 * - 401, 404, etc.: Fails immediately (non-retryable)
 * @param {Function} fn - The async function to call
 * @param {string} label - Human-readable label for logging (e.g. "update status for Naruto")
 * @param {number} baseDelay - Base delay in ms (default 1000)
 * @returns {Promise} Result of the function call
 */
const withRetry = async (fn, label = 'API call', baseDelay = 1000) => {
  let attempt = 0;

  while (true) {
    try {
      return await fn();
    } catch (error) {
      const statusCode = error.response?.status;

      // 500 error: Mutation was likely processed, treat as success
      if (statusCode === 500) {
        console.log(`✅ 500 error for ${label} - considering as success (needs background refresh)`);
        return { _retryPartialSuccess: true };
      }

      // 429 (Rate limit): Infinite retry with exponential backoff (capped at 60s)
      if (statusCode === 429) {
        const delay = Math.min(baseDelay * Math.pow(2, Math.min(attempt, 10)), 60000) + Math.random() * 1000;
        console.log(`⏳ Rate limited (429) for ${label}, retrying after ${Math.round(delay)}ms (attempt ${attempt + 1})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        attempt++;
        continue;
      }

      // 401 (Auth expired): Disconnect OAuth and throw clear message
      if (statusCode === 401) {
        console.error(`🔒 Auth expired (401) for ${label} — disconnecting OAuth`);
        // Emit a global event so the React context can disconnect OAuth
        window.dispatchEvent(new CustomEvent('auth-expired', {
          detail: { message: `Authentication expired while trying to ${label}` }
        }));
        const authError = new Error('Your AniList authorization has expired. Please reconnect OAuth from Settings → OAuth Authorization.');
        authError.isAuthExpired = true;
        throw authError;
      }

      // Network errors: Infinite retry with exponential backoff (capped at 30s)
      const isRetryable =
        error.code === 'ECONNABORTED' ||
        error.code === 'ERR_NETWORK' ||
        error.message?.includes('timeout') ||
        error.message?.includes('network') ||
        error.message?.includes('CORS');

      if (isRetryable) {
        const delay = Math.min(baseDelay * Math.pow(2, Math.min(attempt, 8)), 30000) + Math.random() * 1000;
        console.log(`⏳ Network error for ${label}, retrying after ${Math.round(delay)}ms (attempt ${attempt + 1}/∞)`);
        await new Promise(resolve => setTimeout(resolve, delay));
        attempt++;
        continue;
      }

      // 404: Media not found on AniList
      if (statusCode === 404) {
        console.error(`🔍 Not found (404) for ${label}`);
        const notFoundError = new Error(`Could not find this media on AniList. It may have been removed or the ID is invalid.`);
        notFoundError.response = error.response;
        throw notFoundError;
      }

      // 403: Forbidden (permissions issue)
      if (statusCode === 403) {
        console.error(`🚫 Forbidden (403) for ${label}`);
        const forbiddenError = new Error(`You don't have permission to perform this action. The media may be restricted.`);
        forbiddenError.response = error.response;
        throw forbiddenError;
      }

      // Any other non-retryable error
      console.error(`❌ Unexpected error (${statusCode || 'unknown'}) for ${label}:`, error.message);
      throw error;
    }
  }
};

/**
 * Validate token and get username
 * @param {string} token - AniList OAuth token
 * @returns {Promise<{id: number, name: string}>} User info
 */
export const validateToken = async (token) => {
  const query = `
    query {
      Viewer {
        id
        name
      }
    }
  `;

  const data = await makeGraphQLRequest(query, {}, token);
  return data.Viewer;
};

/**
 * Fetch complete user anime list
 * @param {string} username - AniList username
 * @param {string} token - Optional OAuth token (only needed for private profiles or write operations)
 * @returns {Promise} User's complete anime list
 */
export const fetchUserAnimeList = async (username, token = null) => {
  const query = `
    query ($username: String) {
      MediaListCollection(userName: $username, type: ANIME) {
        user {
          id
          name
          updatedAt
        }
        lists {
          name
          status
          entries {
            id
            mediaId
            status
            score
            progress
            progressVolumes
            repeat
            priority
            private
            notes
            hiddenFromStatusLists
            startedAt {
              year
              month
              day
            }
            completedAt {
              year
              month
              day
            }
            updatedAt
            createdAt
            media {
              id
              idMal
              title {
                romaji
                english
                native
              }
              format
              status
              description
              startDate {
                year
                month
                day
              }
              endDate {
                year
                month
                day
              }
              season
              seasonYear
              episodes
              duration
              chapters
              volumes
              countryOfOrigin
              isLicensed
              source
              hashtag
              trailer {
                id
                site
              }
              updatedAt
              coverImage {
                extraLarge
                large
                medium
                color
              }
              bannerImage
              genres
              synonyms
              averageScore
              meanScore
              popularity
              trending
              tags {
                id
                name
                description
                category
                rank
                isGeneralSpoiler
                isMediaSpoiler
                isAdult
              }
              isFavourite
              isAdult
              nextAiringEpisode {
                id
                airingAt
                timeUntilAiring
                episode
              }
              airingSchedule(notYetAired: false) {
                nodes {
                  episode
                  airingAt
                }
              }
              studios {
                edges {
                  node {
                    id
                    name
                  }
                  isMain
                }
              }
            }
          }
        }
      }
    }
  `;

  const variables = { username };
  const data = await makeGraphQLRequest(query, variables, token);
  return data.MediaListCollection;
};

/**
 * Fetch another user's public anime list (for comparison)
 * Simplified version without auth - only works for public profiles
 * @param {string} username - AniList username to fetch
 * @returns {Promise} User's anime list data
 */
export const fetchPublicUserList = async (username) => {
  const query = `
    query ($username: String) {
      MediaListCollection(userName: $username, type: ANIME) {
        user {
          id
          name
          avatar {
            large
            medium
          }
          bannerImage
          about
          statistics {
            anime {
              count
              episodesWatched
              minutesWatched
              meanScore
            }
          }
        }
        lists {
          name
          status
          entries {
            id
            mediaId
            status
            score
            progress
            repeat
            private
            startedAt {
              year
              month
              day
            }
            completedAt {
              year
              month
              day
            }
            updatedAt
            createdAt
            media {
              id
              idMal
              title {
                romaji
                english
                native
              }
              format
              status
              description
              startDate {
                year
                month
                day
              }
              endDate {
                year
                month
                day
              }
              season
              seasonYear
              episodes
              duration
              coverImage {
                extraLarge
                large
                medium
                color
              }
              bannerImage
              genres
              averageScore
              meanScore
              popularity
              trending
              tags {
                id
                name
                rank
              }
              studios {
                edges {
                  node {
                    id
                    name
                  }
                  isMain
                }
              }
            }
          }
        }
      }
    }
  `;

  const variables = { username };

  try {
    const data = await makeGraphQLRequest(query, variables, null); // No token for public access
    return data.MediaListCollection;
  } catch (error) {
    // Handle specific errors
    if (error.message?.includes('Private')) {
      throw new Error(`User "${username}" has a private profile. Cannot compare with private profiles.`);
    }
    if (error.message?.includes('Not Found') || error.message?.includes('User not found')) {
      throw new Error(`User "${username}" not found on AniList. Please check the username.`);
    }
    throw error;
  }
};

/**
 * Fetch relations for specific anime
 * @param {number} mediaId - Anime media ID
 * @returns {Promise} Anime relations
 */
export const fetchAnimeRelations = async (mediaId) => {
  const query = `
    query ($id: Int) {
      Media(id: $id) {
        id
        relations {
          edges {
            id
            relationType
            node {
              id
              title {
                romaji
                english
              }
              type
              format
              status
              episodes
              coverImage {
                extraLarge
                large
                medium
              }
            }
          }
        }
      }
    }
  `;

  const variables = { id: mediaId };
  const data = await makeGraphQLRequest(query, variables);
  return data.Media;
};

/**
 * Batch fetch relations for multiple anime using GraphQL aliases
 * @param {Array<number>} mediaIds - Array of media IDs (max 20)
 * @returns {Promise} Object with anime IDs as keys and relations as values
 */
export const batchFetchAnimeRelations = async (mediaIds) => {
  if (mediaIds.length === 0) return {};
  if (mediaIds.length > 20) {
    console.warn('BatchFetchAnimeRelations: Limiting to 20 anime per batch');
    mediaIds = mediaIds.slice(0, 20);
  }

  // Build query with aliases
  const fragments = mediaIds.map((id, index) => `
    anime${index}: Media(id: ${id}) {
      id
      relations {
        edges {
          id
          relationType
          node {
            id
            title {
              romaji
              english
            }
            type
            format
            status
            episodes
            startDate {
              year
              month
              day
            }
            coverImage {
              extraLarge
              large
              medium
            }
            averageScore
            description
          }
        }
      }
    }
  `).join('\n');

  const query = `
    query {
      ${fragments}
    }
  `;

  const data = await makeGraphQLRequest(query, {});

  // Transform response into a map
  const result = {};
  Object.keys(data).forEach(key => {
    if (data[key]) {
      result[data[key].id] = data[key];
    }
  });

  return result;
};

/**
 * Add anime to user's list
 * @param {number} mediaId - Media ID to add
 * @param {string} status - Status (CURRENT, PLANNING, COMPLETED, etc.)
 * @param {string} token - OAuth token (required)
 * @returns {Promise} Updated list entry
 */
export const addAnimeToList = async (mediaId, status, token) => {
  if (!token) {
    throw new Error('Authentication required to add anime to list');
  }

  const mutation = `
    mutation ($mediaId: Int, $status: MediaListStatus) {
      SaveMediaListEntry(mediaId: $mediaId, status: $status) {
        id
        mediaId
        status
        score
        progress
        progressVolumes
        repeat
        priority
        private
        notes
        hiddenFromStatusLists
        startedAt {
          year
          month
          day
        }
        completedAt {
          year
          month
          day
        }
        updatedAt
        createdAt
        media {
          id
          idMal
          title {
            romaji
            english
            native
          }
          format
          status
          description
          startDate {
            year
            month
            day
          }
          endDate {
            year
            month
            day
          }
          season
          seasonYear
          episodes
          duration
          chapters
          volumes
          countryOfOrigin
          isLicensed
          source
          hashtag
          trailer {
            id
            site
          }
          updatedAt
          coverImage {
            extraLarge
            large
            medium
            color
          }
          bannerImage
          genres
          synonyms
          averageScore
          meanScore
          popularity
          trending
          tags {
            id
            name
            description
            category
            rank
            isGeneralSpoiler
            isMediaSpoiler
            isAdult
          }
          isFavourite
          isAdult
          nextAiringEpisode {
            id
            airingAt
            timeUntilAiring
            episode
          }
          studios {
            edges {
              node {
                id
                name
              }
              isMain
            }
          }
        }
      }
    }
  `;

  const variables = {
    mediaId,
    status
  };

  const data = await makeGraphQLRequest(mutation, variables, token);
  return data.SaveMediaListEntry;
};

/**
 * Update anime status in user's list
 * @param {number} mediaId - Media ID to update
 * @param {string} newStatus - New status (CURRENT, PLANNING, COMPLETED, PAUSED, DROPPED)
 * @param {string} token - OAuth token (required)
 * @returns {Promise} Updated list entry
 */
export const updateAnimeStatus = async (mediaId, newStatus, token) => {
  if (!token) {
    throw new Error('Authentication required to update anime status');
  }

  const mutation = `
    mutation ($mediaId: Int, $status: MediaListStatus) {
      SaveMediaListEntry(mediaId: $mediaId, status: $status) {
        id
        mediaId
        status
        updatedAt
      }
    }
  `;

  const variables = {
    mediaId,
    status: newStatus
  };

  return withRetry(
    () => makeGraphQLRequest(mutation, variables, token),
    `update status to ${newStatus} for media ${mediaId}`
  );
};

/**
 * Update anime episode progress in user's list
 * @param {number} mediaId - Media ID to update
 * @param {number} progress - New episode progress count
 * @param {string} token - OAuth token (required)
 * @returns {Promise} Updated list entry
 */
export const updateAnimeProgress = async (mediaId, progress, token) => {
  if (!token) {
    throw new Error('Authentication required to update anime progress');
  }

  const mutation = `
    mutation ($mediaId: Int, $progress: Int) {
      SaveMediaListEntry(mediaId: $mediaId, progress: $progress) {
        id
        mediaId
        progress
        status
        updatedAt
      }
    }
  `;

  const variables = {
    mediaId,
    progress
  };

  return withRetry(
    () => makeGraphQLRequest(mutation, variables, token),
    `update progress to ${progress} for media ${mediaId}`
  );
};

/**
 * Add anime to list with retry logic and rate limiting
 * Infinite retries for 429, treats 500 as success with background refresh
 * @param {number} mediaId - Media ID to add
 * @param {string} status - Status (CURRENT, PLANNING, COMPLETED, etc.)
 * @param {string} token - OAuth token (required)
 * @param {number} maxRetries - Maximum number of retries for non-429 errors (default 3)
 * @param {number} baseDelay - Base delay in ms (default 1000)
 * @param {Function} onProgress - Progress callback
 * @returns {Promise} Result object with success flag and optional refresh flag
 */
export const addAnimeWithRetry = async (mediaId, status, token, maxRetries = 3, baseDelay = 1000, onProgress = null) => {
  let attempt = 0;

  while (true) { // Infinite loop - breaks on success or non-retryable error
    try {
      if (onProgress) {
        onProgress({ attempt: attempt + 1, mediaId });
      }

      // Try to add the anime
      const result = await addAnimeToList(mediaId, status, token);

      if (onProgress) {
        onProgress({ success: true, mediaId, result });
      }

      return { success: true, result };

    } catch (error) {
      const statusCode = error.response?.status;

      // 500 error: Anime WAS added, just response failed - treat as success!
      if (statusCode === 500) {
        console.log(`✅ 500 error for anime ${mediaId} - considering as success (anime was added, needs background refresh)`);
        if (onProgress) {
          onProgress({ partialSuccess: true, mediaId, needsRefresh: true });
        }
        return { success: true, partialSuccess: true, needsRefresh: true, mediaId };
      }

      // 429 (Rate limit): INFINITE RETRY with exponential backoff (capped at 60s)
      if (statusCode === 429) {
        const delay = Math.min(baseDelay * Math.pow(2, Math.min(attempt, 10)), 60000) + Math.random() * 1000;
        console.log(`⏳ Rate limited (429) for anime ${mediaId}, retrying after ${Math.round(delay)}ms (attempt ${attempt + 1})`);

        if (onProgress) {
          onProgress({ retrying: true, rateLimited: true, attempt: attempt + 1, delay, mediaId });
        }

        await new Promise(resolve => setTimeout(resolve, delay));
        attempt++;
        continue; // Continue infinite loop for rate limits
      }

      // Other retryable errors (network issues, CORS): INFINITE RETRY with backoff
      const isRetryable =
        error.code === 'ECONNABORTED' ||
        error.code === 'ERR_NETWORK' ||
        error.message?.includes('timeout') ||
        error.message?.includes('network') ||
        error.message?.includes('CORS');

      if (isRetryable) {
        // Infinite retry with exponential backoff (capped at 30s)
        const delay = Math.min(baseDelay * Math.pow(2, Math.min(attempt, 8)), 30000) + Math.random() * 1000;
        console.log(`⏳ Network error for anime ${mediaId}, retrying after ${Math.round(delay)}ms (attempt ${attempt + 1}/∞)`);

        if (onProgress) {
          onProgress({ retrying: true, networkError: true, attempt: attempt + 1, delay, mediaId, error });
        }

        await new Promise(resolve => setTimeout(resolve, delay));
        attempt++;
        continue; // Continue infinite loop for network errors
      }

      // Non-retryable errors (401, 404, etc.) - throw immediately
      console.error(`❌ Non-retryable error for anime ${mediaId}:`, error.message);
      if (onProgress) {
        onProgress({ success: false, mediaId, error });
      }
      throw error;
    }
  }
};

// ==================== MANGA API FUNCTIONS ====================

/**
 * Fetch complete user manga list
 * @param {string} username - AniList username
 * @param {string} token - Optional OAuth token
 * @returns {Promise} User's complete manga list
 */
export const fetchUserMangaList = async (username, token = null) => {
  const query = `
    query ($username: String) {
      MediaListCollection(userName: $username, type: MANGA) {
        user {
          id
          name
          updatedAt
        }
        lists {
          name
          status
          entries {
            id
            mediaId
            status
            score
            progress
            progressVolumes
            repeat
            priority
            private
            notes
            hiddenFromStatusLists
            startedAt {
              year
              month
              day
            }
            completedAt {
              year
              month
              day
            }
            updatedAt
            createdAt
            media {
              id
              idMal
              title {
                romaji
                english
                native
              }
              format
              status
              description
              startDate {
                year
                month
                day
              }
              endDate {
                year
                month
                day
              }
              chapters
              volumes
              countryOfOrigin
              isLicensed
              source
              updatedAt
              coverImage {
                extraLarge
                large
                medium
                color
              }
              bannerImage
              genres
              synonyms
              averageScore
              meanScore
              popularity
              trending
              tags {
                id
                name
                description
                category
                rank
                isGeneralSpoiler
                isMediaSpoiler
                isAdult
              }
              isFavourite
              isAdult
              relations {
                edges {
                  relationType
                  node {
                    id
                    type
                    format
                    status
                    chapters
                    volumes
                    averageScore
                    title {
                      romaji
                      english
                      native
                    }
                    coverImage {
                      extraLarge
                      large
                      medium
                    }
                    startDate {
                      year
                      month
                      day
                    }
                    description
                    genres
                    bannerImage
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const variables = { username };
  const data = await makeGraphQLRequest(query, variables, token);
  return data.MediaListCollection;
};

/**
 * Fetch another user's public manga list (for comparison)
 * @param {string} username - AniList username to fetch
 * @returns {Promise} User's manga list data
 */
export const fetchPublicMangaList = async (username) => {
  const query = `
    query ($username: String) {
      MediaListCollection(userName: $username, type: MANGA) {
        user {
          id
          name
          avatar {
            large
            medium
          }
          bannerImage
          about
          statistics {
            manga {
              count
              chaptersRead
              volumesRead
              meanScore
            }
          }
        }
        lists {
          name
          status
          entries {
            id
            mediaId
            status
            score
            progress
            progressVolumes
            repeat
            private
            startedAt {
              year
              month
              day
            }
            completedAt {
              year
              month
              day
            }
            updatedAt
            createdAt
            media {
              id
              idMal
              title {
                romaji
                english
                native
              }
              format
              status
              description
              startDate {
                year
                month
                day
              }
              endDate {
                year
                month
                day
              }
              chapters
              volumes
              coverImage {
                extraLarge
                large
                medium
                color
              }
              bannerImage
              genres
              averageScore
              meanScore
              popularity
              relations {
                edges {
                  relationType
                  node {
                    id
                    type
                  }
                }
              }
              trending
              tags {
                id
                name
                rank
              }
            }
          }
        }
      }
    }
  `;

  const variables = { username };

  try {
    const data = await makeGraphQLRequest(query, variables, null);
    return data.MediaListCollection;
  } catch (error) {
    if (error.message?.includes('Private')) {
      throw new Error(`User "${username}" has a private profile. Cannot compare with private profiles.`);
    }
    if (error.message?.includes('Not Found') || error.message?.includes('User not found')) {
      throw new Error(`User "${username}" not found on AniList. Please check the username.`);
    }
    throw error;
  }
};

/**
 * Update manga chapter progress in user's list
 * @param {number} mediaId - Media ID to update
 * @param {number} progress - New chapter progress count
 * @param {string} token - OAuth token (required)
 * @returns {Promise} Updated list entry
 */
export const updateMangaProgress = async (mediaId, progress, token) => {
  if (!token) {
    throw new Error('Authentication required to update manga progress');
  }

  const mutation = `
    mutation ($mediaId: Int, $progress: Int) {
      SaveMediaListEntry(mediaId: $mediaId, progress: $progress) {
        id
        mediaId
        progress
        progressVolumes
        status
        updatedAt
      }
    }
  `;

  const variables = {
    mediaId,
    progress
  };

  return withRetry(
    () => makeGraphQLRequest(mutation, variables, token),
    `update chapter progress to ${progress} for manga ${mediaId}`
  );
};

/**
 * Update manga volume progress in user's list
 * @param {number} mediaId - Media ID to update
 * @param {number} progressVolumes - New volume progress count
 * @param {string} token - OAuth token (required)
 * @returns {Promise} Updated list entry
 */
export const updateMangaVolumeProgress = async (mediaId, progressVolumes, token) => {
  if (!token) {
    throw new Error('Authentication required to update manga volume progress');
  }

  const mutation = `
    mutation ($mediaId: Int, $progressVolumes: Int) {
      SaveMediaListEntry(mediaId: $mediaId, progressVolumes: $progressVolumes) {
        id
        mediaId
        progress
        progressVolumes
        status
        updatedAt
      }
    }
  `;

  const variables = {
    mediaId,
    progressVolumes
  };

  return withRetry(
    () => makeGraphQLRequest(mutation, variables, token),
    `update volume progress to ${progressVolumes} for manga ${mediaId}`
  );
};

/**
 * Update user's score for an anime/manga entry
 * @param {number} mediaId - Media ID to update
 * @param {number} scoreRaw - Score on 0-100 scale (AniList internal)
 * @param {string} token - OAuth token (required)
 * @returns {Promise} Updated list entry
 */
export const updateMediaScore = async (mediaId, scoreRaw, token) => {
  if (!token) {
    throw new Error('Authentication required to update score');
  }

  const mutation = `
    mutation ($mediaId: Int, $scoreRaw: Int) {
      SaveMediaListEntry(mediaId: $mediaId, scoreRaw: $scoreRaw) {
        id
        mediaId
        score(format: POINT_100)
        status
        updatedAt
      }
    }
  `;

  const variables = {
    mediaId,
    scoreRaw
  };

  return withRetry(
    () => makeGraphQLRequest(mutation, variables, token),
    `update score to ${scoreRaw} for media ${mediaId}`
  );
};
